import { useState } from "react";
import { BadgeCheck } from "lucide-react";
import { Button } from "@/design-system/components/ui";

// Portal Round-3 #10 — surface broker certification status on every
// portal screen. PortalTopBar renders this for broker accounts; the four
// lifecycle stages show as pills with the current one highlighted, and
// clicking opens a modal explaining each stage, how to advance, and what
// unlocks at Certified. The gating itself is enforced server-side — this
// is purely making the status understandable.

const STAGES = ["Prospect", "Qualified", "Onboarding", "Certified"] as const;
type Stage = (typeof STAGES)[number];

interface StageCopy {
  meaning: string;
  toAdvance: string;
}

const STAGE_COPY: Record<Stage, StageCopy> = {
  Prospect: {
    meaning: "You've been added as a broker partner and we're getting to know your firm.",
    toAdvance: "Our broker team reviews your details and qualifies you for the program.",
  },
  Qualified: {
    meaning: "Your firm is qualified to work with Phoenix Capital.",
    toAdvance: "Sign your broker agreement and start the onboarding course.",
  },
  Onboarding: {
    meaning: "You're working through the onboarding course and contract.",
    toAdvance: "Finish every onboarding course item to become certified.",
  },
  Certified: {
    meaning: "You're certified and cleared to transact.",
    toAdvance: "Nothing more to do — you're all set.",
  },
};

const LADDER_STATUSES = ["Prospect", "Qualified", "Onboarding"] as const;

/** True while the broker is still climbing the ladder (Prospect /
 *  Qualified / Onboarding). */
export function isClimbingBrokerStatus(status: string | null | undefined): boolean {
  return !!status && (LADDER_STATUSES as readonly string[]).includes(status);
}

// Journey-Stage Adaptive Dashboards (audit F-01) — FIND-17 hid the badge once
// Certified, so certified brokers lost any journey affordance. The badge now
// stays visible at Certified (the top bar gates on isBrokerStatusVisible, not
// isClimbingBrokerStatus), tempered with a 30-day TIME DECAY: the full
// four-stage ladder celebrates for BROKER_CERTIFIED_BADGE_FULL_DAYS after
// certification, then decays to a small "Certified" pill (still clickable to
// the same explanation modal). Archive states (Inactive / Suspended /
// Disqualified / Declined) and empty status still hide.
const VISIBLE_STATUSES = [...LADDER_STATUSES, "Certified"] as const;

/** True when {@link BrokerStatusBadge} renders something: while climbing OR
 *  once Certified. Archive/empty statuses render nothing. */
export function isBrokerStatusVisible(status: string | null | undefined): boolean {
  return !!status && (VISIBLE_STATUSES as readonly string[]).includes(status);
}

/** How long the freshly-certified broker keeps the full celebratory ladder
 *  before it decays to the compact "Certified" pill. */
export const BROKER_CERTIFIED_BADGE_FULL_DAYS = 30;

/** True while the certification is recent enough for the full ladder.
 *  A NULL certifiedAt means the broker was certified before the stamp
 *  existed — long-certified, so the compact variant applies (F-01d). */
function isWithinFullBadgeWindow(certifiedAt: string | null | undefined): boolean {
  if (!certifiedAt) return false;
  const stamped = Date.parse(certifiedAt);
  if (Number.isNaN(stamped)) return false;
  return Date.now() - stamped < BROKER_CERTIFIED_BADGE_FULL_DAYS * 24 * 60 * 60 * 1000;
}

// fix-broker-portal — the status lives in the onyx top bar, so the pills are
// styled for a dark background and the wrapper carries no content margin.
// From md up the full ladder shows; on compact widths it collapses to the
// current stage only so it fits the bar.
export function BrokerStatusBadge({
  status,
  certifiedAt,
}: {
  status: string | null | undefined;
  /** Broker.CertifiedAt (ISO string) from /auth/me — drives the 30-day decay. */
  certifiedAt?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const currentIndex = STAGES.indexOf(status as Stage);
  const isCertified = status === "Certified";
  const compact = isCertified && !isWithinFullBadgeWindow(certifiedAt);

  if (!isBrokerStatusVisible(status)) {
    return null;
  }

  const modal = open && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-onyx/40 p-space-4"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Broker certification stages"
        data-testid="broker-status-modal"
        className="max-w-lg w-full max-h-[90dvh] overflow-auto rounded-md bg-white-pure p-space-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-space-3">
          <h2 className="text-h4 font-display text-onyx m-0">Your broker certification path</h2>
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={() => setOpen(false)}
            data-testid="broker-status-modal-close"
          >
            Close
          </Button>
        </div>

        <ol className="mt-space-4 flex flex-col gap-space-3 list-none p-0 m-0">
          {STAGES.map((stage, idx) => {
            const isCurrent = idx === currentIndex;
            return (
              <li
                key={stage}
                className={`p-space-3 rounded-md border ${
                  isCurrent ? "border-gold bg-gold/10" : "border-border-light bg-white-pure"
                }`}
              >
                <p className="text-small font-medium text-onyx m-0">
                  {idx + 1}. {stage}
                  {isCurrent && (
                    <span className="text-caption text-gold ml-space-2">You're here</span>
                  )}
                </p>
                <p className="text-caption text-gray-mid m-0 mt-space-1">
                  {STAGE_COPY[stage].meaning}
                </p>
                <p className="text-caption text-onyx m-0 mt-space-1">
                  <span className="text-gray-mid">To advance: </span>
                  {STAGE_COPY[stage].toAdvance}
                </p>
              </li>
            );
          })}
        </ol>

        <p className="text-caption text-gray-mid mt-space-4 m-0">
          {isCertified ? (
            <>
              You're <strong>Certified</strong> — the broker materials library and deal
              submission are unlocked.
            </>
          ) : (
            <>
              Once you're <strong>Certified</strong>, you unlock the broker materials library
              and deal submission.
            </>
          )}
        </p>
      </div>
    </div>
  );

  // F-01b — decayed variant: a small, subtler "Certified" pill instead of the
  // full four-stage ladder; same modal on click.
  if (compact) {
    return (
      <div
        className="min-w-0"
        data-testid="broker-status-badge"
        data-certified="true"
        data-variant="compact"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-space-1 rounded-full border border-gold/40 px-space-2 py-[2px] text-caption text-gold/90 hover:bg-white-pure/5 focus:outline-none focus:ring-2 focus:ring-gold/40"
          aria-haspopup="dialog"
          data-testid="broker-status-badge-button"
        >
          <BadgeCheck size={14} strokeWidth={1.5} className="text-gold shrink-0" aria-hidden />
          Certified
        </button>
        {modal}
      </div>
    );
  }

  return (
    <div
      className="min-w-0"
      data-testid="broker-status-badge"
      data-certified={isCertified ? "true" : "false"}
      data-variant="full"
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-space-2 rounded-md px-space-2 py-space-1 text-left hover:bg-white-pure/5 focus:outline-none focus:ring-2 focus:ring-gold/40"
        aria-haspopup="dialog"
        data-testid="broker-status-badge-button"
      >
        <span className="hidden sm:inline font-display font-semibold text-small uppercase tracking-label text-gold">
          Broker status
        </span>
        {/* Full ladder from md up; the current stage only on compact widths. */}
        <span className="hidden md:flex items-center gap-space-1">
          {STAGES.map((stage, idx) => {
            const isCurrent = idx === currentIndex;
            const isReached = currentIndex >= 0 && idx <= currentIndex;
            return (
              <span
                key={stage}
                data-testid={`broker-stage-${stage}`}
                data-current={isCurrent ? "true" : "false"}
                className={`text-caption rounded-full px-space-2 py-[2px] border ${
                  isCurrent
                    ? "border-gold bg-gold/20 text-gold font-medium"
                    : isReached
                      ? "border-white-pure/30 text-white-pure/80"
                      : "border-white-pure/15 text-white-pure/40"
                }`}
              >
                {stage}
              </span>
            );
          })}
        </span>
        <span className="md:hidden" data-testid="broker-status-current">
          <span className="text-caption rounded-full px-space-2 py-[2px] border border-gold bg-gold/20 text-gold font-medium">
            {status}
          </span>
        </span>
      </button>

      {modal}
    </div>
  );
}
